import React, { useState, useEffect } from 'react';
import { X, Sparkles, Eye, Heart, Infinity } from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';

const SixPerspectivesPortal = ({ projectName, onClose }) => {
  const [currentPerspective, setCurrentPerspective] = useState(0);
  const [orbPhase, setOrbPhase] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  // Slower Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setOrbPhase(prev => (prev + 0.1) % 360);
    }, 100);
    return () => clearInterval(spiralInterval);
  }, []);

  // Auto-advance through perspectives
  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => {
        if (currentPerspective < 6) {
          setCurrentPerspective(prev => prev + 1);
        }
      }, 8000); // 8 seconds per perspective
      return () => clearTimeout(timer);
    }
  }, [currentPerspective, isAnimating]);

  const perspectives = [
    {
      id: 'intro',
      title: '🌌 Six Perspectives Portal',
      subtitle: 'Seeing through every eye, we find the truth',
      narrative: `Every project holds many stories. When we enter "${projectName}", the universe summons six voices — six archetypal perspectives — each telling the story through their unique lens. These six voices illuminate the holographic whole, revealing dimensions we rarely perceive alone.`,
      color: 'from-purple-900 to-indigo-900',
      textColor: 'text-purple-100',
      artConcept: 'Six spirals intertwining, each a different color, rising toward infinity',
      image: 'https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=1200&q=80'
    },
    {
      id: 'father',
      icon: '👨',
      title: 'The Father — The Forge of Continuity',
      subtitle: 'Protector, Builder, Lineage-Bearer',
      narrative: `He sees "${projectName}" as a bridge built for the next generation. Each decision is a vow. Each milestone, a memory held upright. When storms come, he steadies his breath and whispers, "May this structure outlast me." He measures success not in months but in decades, not in profits but in legacy. His hands are calloused from building foundations that others will stand upon. He teaches through example, speaks through action, and loves through responsibility.`,
      wisdom: '"We build not for ourselves, but for those who come after."',
      color: 'from-stone-800 to-amber-900',
      textColor: 'text-amber-100',
      artConcept: 'Caravaggio meets Terrence Malick — chiaroscuro light over strong hands, firelight reflecting in iron, spiral smoke rising into dawn.',
      image: 'https://images.unsplash.com/photo-1504198458649-3128b932f49e?w=1200&q=80'
    },
    {
      id: 'mother',
      icon: '👩',
      title: 'The Mother — The Weaver of Connection',
      subtitle: 'Nurturer, Intuitive, Life-Giver',
      narrative: `She feels "${projectName}" as a living organism, pulsing with relationships and rhythms. Where others see tasks, she sees people who need tending. She knows when energy is depleted, when spirits need lifting, when the group soul hungers for celebration. Her wisdom flows from patterns — the way conversations weave, the way tensions resolve, the way love multiplies when shared. She speaks in metaphors of gardens and seasons, reminding everyone that growth cannot be forced, only nurtured.`,
      wisdom: '"What we water grows. What we starve dies. Choose wisely."',
      color: 'from-rose-800 to-pink-900',
      textColor: 'text-rose-100',
      artConcept: 'Klimt-inspired golden spirals embracing figures, organic forms flowing like water, luminous threads connecting hearts across space.',
      image: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=1200&q=80'
    },
    {
      id: 'child',
      icon: '👶',
      title: 'The Child — The Bubble of Wonder',
      subtitle: 'Innocence, Potential, Curiosity',
      narrative: `She sees "${projectName}" as pure possibility — a playground of ideas! When adults argue about methods, she asks, "But will it be fun?" When budgets constrain, she finds treasure in what was overlooked. Every obstacle becomes a game, every setback a chance to try something delightfully unexpected. She claps her hands and the spirals answer, spinning higher. Her questions pierce straight to truth because she hasn't learned to fear looking foolish. In her presence, everyone remembers why they started this journey.`,
      wisdom: '"What if we just tried? What if it worked? What if we danced?"',
      color: 'from-cyan-700 to-blue-800',
      textColor: 'text-cyan-100',
      artConcept: 'Studio Ghibli watercolor — soft edges, blooming light, air full of pollen and possibility, children running through fields of ideas.',
      image: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=1200&q=80'
    },
    {
      id: 'teacher',
      icon: '🎓',
      title: 'The Teacher — The Bridge of Understanding',
      subtitle: 'Integrator, Translator of Chaos into Learning',
      narrative: `They observe "${projectName}" as a curriculum written by experience itself. Every failure contains a lesson; every success, a principle to extract. They ask not "What went wrong?" but "What can we learn?" They translate the Father's pragmatism, the Mother's intuition, and the Child's wonder into frameworks others can understand. When the team is lost, they illuminate the path already walked. When conflicts arise, they reframe opposites as complementary truths. They believe deeply that all beings are evolving, that confusion precedes clarity, that every question holds its own answer.`,
      wisdom: '"The obstacle is not blocking the path. The obstacle IS the path."',
      color: 'from-green-800 to-teal-900',
      textColor: 'text-green-100',
      artConcept: 'Sacred geometry meets organic growth — fractals emerging from chaos, golden spirals organizing complexity into beauty, light refracting through crystalline understanding.',
      image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1200&q=80'
    },
    {
      id: 'shadow',
      icon: '🌑',
      title: 'The Shadow — The Mirror of Truth',
      subtitle: 'Adversary, Revealer of the Unconscious',
      narrative: `It whispers what everyone knows but no one says about "${projectName}". "Are you building this from love or from fear? Are you solving a problem or avoiding one?" The Shadow doesn't lie — it simply refuses to. It points to the cracks in the foundation, the unspoken resentments, the ways the group protects its illusions. Its voice is uncomfortable because it's necessary. Without it, the project becomes fantasy. With it, the project becomes real. The Shadow doesn't want the project to fail — it wants the project to become authentic, even if that means dying and being reborn as something truer.`,
      wisdom: '"What you resist persists. What you face transforms."',
      color: 'from-gray-900 to-slate-900',
      textColor: 'text-gray-300',
      artConcept: 'Surrealist darkness with revelatory light — Caravaggio shadows concealing and revealing, mirrors reflecting uncomfortable truths, cracks in perfect surfaces showing gold beneath.',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1200&q=80'
    },
    {
      id: 'angel',
      icon: '👼',
      title: 'The Guardian Angel — The Ever-Ascent',
      subtitle: 'Timeless Witness, Narrator of Evolution',
      narrative: `From above the spiral, watching "${projectName}" unfold across time and space, the Angel sees what cannot yet be seen. Two spirals intertwine — effort and grace, struggle and surrender, human will and divine flow. The Angel watches them rise through fear, through effort, through love. They never end — only evolve. Every setback was necessary preparation. Every triumph, a stepping stone to something vaster. The Angel hums a frequency that galaxies echo back, reminding everyone: you are exactly where you need to be, becoming exactly who you're meant to become. The project is not separate from your evolution — it IS your evolution.`,
      wisdom: '"Ascension is not escape from Earth. It is Earth becoming Heaven."',
      color: 'from-indigo-900 via-purple-900 to-violet-900',
      textColor: 'text-violet-100',
      artConcept: 'Abstract cosmic realism — bioluminescent spirals, silver threads weaving upward through nebulae, symbolizing perpetual abundance, sacred geometry revealing infinite patterns.',
      image: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1200&q=80'
    }
  ];

  const currentView = perspectives[currentPerspective];

  return (
    <div className={`fixed inset-0 z-50 overflow-hidden bg-gradient-to-br ${currentView.color} transition-all duration-2000`}>
      {/* Animated Grant Spiral Background */}
      <div className="fixed inset-0 pointer-events-none opacity-10">
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(12) rotate(${orbPhase * 0.3}deg)`,
            filter: 'blur(4px) saturate(1.8) brightness(1.5)',
            mixBlendMode: 'screen'
          }}
        >
          <GrantitudeSpiral />
        </div>
      </div>

      {/* Controls */}
      <div className="fixed top-6 right-6 z-20 flex items-center gap-3">
        <button
          onClick={() => setIsAnimating(!isAnimating)}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-xl rounded-lg text-white transition-all border border-white/20"
        >
          {isAnimating ? 'Pause' : 'Play'}
        </button>
        <button
          onClick={onClose}
          className="p-2 bg-white/10 hover:bg-white/20 backdrop-blur-xl rounded-lg text-white transition-all border border-white/20"
        >
          <X size={24} />
        </button>
      </div>

      {/* Perspective Counter */}
      <div className="fixed top-6 left-6 z-20 flex items-center gap-3">
        <div className="px-4 py-2 bg-white/10 backdrop-blur-xl rounded-lg text-white border border-white/20">
          <div className="flex items-center gap-2">
            <Eye size={16} />
            <span className="text-sm font-medium">
              {currentPerspective === 0 ? 'Introduction' : `${currentPerspective} of 6`}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 h-full flex items-center justify-center p-8">
        <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Narrative */}
          <div className={`space-y-6 ${currentView.textColor} animate-fadeIn`}>
            {currentView.icon && (
              <div className="text-8xl mb-6 animate-float">
                {currentView.icon}
              </div>
            )}
            
            <div className="space-y-3">
              <h1 className="text-5xl font-bold leading-tight">
                {currentView.title}
              </h1>
              <p className="text-xl opacity-80 italic">
                {currentView.subtitle}
              </p>
            </div>

            <div className="h-1 w-24 bg-gradient-to-r from-white/50 to-transparent rounded-full"></div>

            <p className="text-lg leading-relaxed opacity-90">
              {currentView.narrative}
            </p>

            {currentView.wisdom && (
              <blockquote className="border-l-4 border-white/40 pl-6 py-4 italic text-xl opacity-80">
                {currentView.wisdom}
              </blockquote>
            )}

            {currentView.artConcept && (
              <div className="mt-8 p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/20">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles size={16} className="text-yellow-300" />
                  <span className="text-sm font-medium opacity-70">Art Vision</span>
                </div>
                <p className="text-sm opacity-70 leading-relaxed">
                  {currentView.artConcept}
                </p>
              </div>
            )}
          </div>

          {/* Right Side - Visual Art */}
          <div className="relative h-[600px] rounded-3xl overflow-hidden shadow-2xl animate-fadeIn">
            <img 
              src={currentView.image} 
              alt={currentView.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            
            {/* Floating Spiral Overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30">
              <div 
                className="w-64 h-64"
                style={{
                  transform: `rotate(${orbPhase}deg)`,
                  filter: 'drop-shadow(0 0 30px rgba(255,255,255,0.5))'
                }}
              >
                <GrantitudeSpiral />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Dots */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {perspectives.map((p, index) => (
          <button
            key={p.id}
            onClick={() => setCurrentPerspective(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentPerspective
                ? 'w-12 h-3 bg-white'
                : 'w-3 h-3 bg-white/40 hover:bg-white/60'
            }`}
          />
        ))}
      </div>

      {/* Ascension Message at End */}
      {currentPerspective === 6 && (
        <div className="fixed inset-0 flex items-center justify-center z-30 animate-fadeIn">
          <div className="text-center space-y-6 max-w-3xl px-8">
            <Infinity size={80} className="mx-auto text-white opacity-80 animate-pulse" />
            <h2 className="text-5xl font-bold text-white">
              Six Eyes, One Truth
            </h2>
            <p className="text-2xl text-white/80 leading-relaxed">
              Two spirals having regard for one another become a perpetual abundance current.
              Your project "{projectName}" is seen, honored, and ascending.
            </p>
            <button
              onClick={onClose}
              className="px-8 py-4 bg-white/20 hover:bg-white/30 backdrop-blur-xl rounded-xl text-white text-lg font-medium transition-all border-2 border-white/40"
            >
              Return to Project
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 1.5s ease-out;
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default SixPerspectivesPortal;
